import { Injectable, OnDestroy, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AuthService } from './auth.service';
import { ChatService } from './chat.service';
import { NotificationService } from './notification.service';
import { PropertyService } from './property.service';
import { OrganizationService } from './organization.service';
import * as Parse from 'parse';

/**
 * Service to manage chat notifications globally for agents
 * This ensures agents receive notifications even without visiting the chat page
 */
@Injectable({
  providedIn: 'root'
})
export class ChatNotificationManagerService implements OnDestroy {
  private messageThreadSubscription: Parse.LiveQuerySubscription | undefined;
  private messageSubscriptions: Map<string, Parse.LiveQuerySubscription> = new Map();
  private isInitialized = false;
  private openThreadIds: Set<string> = new Set();
  private notificationClickSubscription: Subscription | undefined;
  private isNavigating = false;
  private clickHandlerSetup = false;

  constructor(
    private auth: AuthService,
    private chatService: ChatService,
    private notificationService: NotificationService,
    private propertyService: PropertyService,
    private orgService: OrganizationService,
    private router: Router,
    private ngZone: NgZone
  ) {
    // Subscribe to notification clicks to handle navigation
    // Only setup once to avoid multiple subscriptions
    if (!this.clickHandlerSetup) {
      this.setupNotificationClickHandler();
      this.clickHandlerSetup = true;
    }
  }

  /**
   * Initialize agent chat notifications
   * Should be called when the app loads for authenticated agents
   */
  async initializeAgentNotifications(): Promise<void> {
    console.log('[ChatNotificationManager] initializeAgentNotifications called', {
      isUserLoggedIn: this.auth.isUserLoggedIn,
      isInitialized: this.isInitialized,
      hasOrganization: !!this.orgService.organization
    });

    // Only initialize for logged-in users (agents), not customers
    if (!this.auth.isUserLoggedIn || this.isInitialized) {
      console.log('[ChatNotificationManager] Skipping initialization', {
        reason: !this.auth.isUserLoggedIn ? 'not logged in' : 'already initialized'
      });
      return;
    }

    this.isInitialized = true;

    // Wait for organization to be available
    if (!this.orgService.organization) {
      console.warn('[ChatNotificationManager] Organization not available, skipping notification setup');
      return;
    }

    console.log('[ChatNotificationManager] Starting notification setup for organization:', this.orgService.organization.id);

    // Subscribe to property message threads for all properties this agent manages
    await this.setupGlobalMessageThreadSubscription();
  }

  /**
   * Mark a thread as open (when modal is opened)
   * This prevents notifications for that thread while it's open and focused
   */
  markThreadAsOpen(threadId: string): void {
    this.openThreadIds.add(threadId);
  }

  /**
   * Mark a thread as closed (when modal is closed)
   * This re-enables notifications for that thread
   */
  markThreadAsClosed(threadId: string): void {
    this.openThreadIds.delete(threadId);
  }

  /**
   * Check if a thread is currently open
   */
  isThreadOpen(threadId: string): boolean {
    return this.openThreadIds.has(threadId);
  }

  /**
   * Subscribe to all message threads for all properties
   */
  private async setupGlobalMessageThreadSubscription(): Promise<void> {
    try {
      // Query all message threads for this agent directly (no property filter)
      const query = new Parse.Query('PropertyMessageThread');
      query.equalTo('agent', this.auth.currentUser);
      query.equalTo('organization', this.orgService.organization);
      query.include('customer');
      query.include('property');
      query.limit(1000); // Adjust as needed

      const threads = await query.find();
      console.log(`[ChatNotificationManager] Found ${threads.length} total threads for agent`);

      // Subscribe to messages for each thread
      for (const thread of threads) {
        await this.subscribeToThreadMessages(thread);
      }

      // Also subscribe to new threads being created for this agent
      const threadSubscription = await query.subscribe();
      threadSubscription.on('create', async (newThread: Parse.Object) => {
        console.log('[ChatNotificationManager] New thread created:', newThread.id);
        await this.subscribeToThreadMessages(newThread);
      });

      // Store the thread subscription so we can clean it up later
      this.messageThreadSubscription = threadSubscription;

      console.log(`[ChatNotificationManager] Subscribed to ${this.messageSubscriptions.size} message threads`);
    } catch (error: any) {
      console.error('[ChatNotificationManager] Error setting up global subscription:', error);
    }
  }

  /**
   * Subscribe to messages for a specific thread
   */
  private async subscribeToThreadMessages(thread: Parse.Object): Promise<void> {
    const threadId = thread.id;

    // Skip if already subscribed
    if (this.messageSubscriptions.has(threadId)) {
      return;
    }

    try {
      const subscription = await this.chatService.subscribeToMessages(thread);
      console.log(`[ChatNotificationManager] Subscribed to messages for thread ${threadId}`);

      subscription.on('create', (message: Parse.Object) => {
        console.log(`[ChatNotificationManager] New message received in thread ${threadId}`, {
          from: message.get('from'),
          isThreadOpen: this.isThreadOpen(threadId),
          isDocumentFocused: this.notificationService.isDocumentFocused()
        });

        // Show notification if:
        // 1. Message is from customer
        // 2. Thread is NOT open OR document is not focused
        if (message.get('from') === 'customer' &&
            (!this.isThreadOpen(threadId) || !this.notificationService.isDocumentFocused())) {

          const customerName = thread.get('customerName') || 'Customer';
          const property = thread.get('property');
          const propertyId = property?.id;

          console.log('[ChatNotificationManager] Showing notification for message from:', customerName);

          this.notificationService.showChatNotification({
            threadId: threadId,
            senderName: customerName,
            messagePreview: message.get('message'),
            propertyAddress: thread.get('propertyAddress'),
            propertyId: propertyId,
            timestamp: message.createdAt
          });
        }
      });

      this.messageSubscriptions.set(threadId, subscription);
    } catch (error: any) {
      console.error(`[ChatNotificationManager] Failed to subscribe to thread ${threadId}:`, error);
    }
  }

  /**
   * Setup handler for notification clicks
   */
  private setupNotificationClickHandler(): void {
    this.notificationClickSubscription = this.notificationService.notificationClicked$
      .subscribe(
        (data: {threadId: string, propertyId?: string}) => {
          console.log('[ChatNotificationManager] Notification clicked (RAW):', data);

          // Prevent navigation if already navigating
          if (this.isNavigating) {
            console.log('[ChatNotificationManager] Already navigating, skipping');
            return;
          }

          if (!data.propertyId) {
            console.warn('[ChatNotificationManager] No propertyId provided for navigation');
            return;
          }

          // Get organization ID for routing
          const orgId = this.orgService.organization?.id;
          if (!orgId) {
            console.error('[ChatNotificationManager] Organization ID not available');
            return;
          }

          const targetUrl = `/${orgId}/dashboard/properties/${data.propertyId}/marketing`;
          console.log('[ChatNotificationManager] Current URL:', this.router.url);
          console.log('[ChatNotificationManager] Target URL:', targetUrl);

          // Check if we're already on the target page
          if (this.router.url === targetUrl) {
            console.log('[ChatNotificationManager] Already on target page, skipping navigation');
            return;
          }

          console.log('[ChatNotificationManager] Setting isNavigating = true');
          this.isNavigating = true;

          // Run navigation inside Angular zone to avoid zone warnings
          this.ngZone.run(() => {
            console.log('[ChatNotificationManager] Calling router.navigate()');
            // Navigate to property marketing chat page with organization ID and chat segment
            this.router.navigate([orgId, 'dashboard', 'properties', data.propertyId, 'marketing'], {
              queryParams: { segment: 'chat' }
            })
              .then((success) => {
                console.log('[ChatNotificationManager] Navigation completed. Success:', success);
                // Reset flag after a delay to prevent rapid re-navigation
                setTimeout(() => {
                  console.log('[ChatNotificationManager] Resetting isNavigating flag');
                  this.isNavigating = false;
                }, 1000);
              })
              .catch((error) => {
                console.error('[ChatNotificationManager] Navigation error:', error);
                this.isNavigating = false;
              });
          });
        }
      );
  }

  /**
   * Clean up all subscriptions
   */
  ngOnDestroy(): void {
    if (this.messageThreadSubscription) {
      this.messageThreadSubscription.unsubscribe();
    }

    if (this.notificationClickSubscription) {
      this.notificationClickSubscription.unsubscribe();
    }

    this.messageSubscriptions.forEach(sub => sub.unsubscribe());
    this.messageSubscriptions.clear();
    this.openThreadIds.clear();
    this.isInitialized = false;
  }

  /**
   * Reset the service (useful for logout)
   */
  reset(): void {
    this.ngOnDestroy();
  }
}
